/**
 * File entry operations service.
 * Provides async functions for file entry CRUD operations.
 */

import { fetchApi } from "common/utils/Api";
import { FarmFileUpload, FileEntry } from "model";

/**
 * Create a new file entry.
 */
export async function createFileEntry(
  fileEntry: FileEntry
): Promise<FileEntry> {
  return fetchApi("fileEntry", {
    method: "POST",
    body: JSON.stringify(fileEntry),
  }) as Promise<FileEntry>;
}

/**
 * Delete a file entry.
 */
export async function deleteFileEntry(fileEntryId: string): Promise<void> {
  await fetchApi(`fileEntry/${fileEntryId}`, {
    method: "DELETE",
  });
}

/**
 * Update a file entry.
 * Only sends fields allowed by the backend update schema.
 */
export async function updateFileEntry(
  fileEntry: FileEntry
): Promise<FileEntry> {
  // Only send fields allowed by the backend update schema
  const updatePayload = {
    fileUploadId: fileEntry.fileUploadId,
    fileDescription: fileEntry.fileDescription,
    associatedUserId: fileEntry.associatedUserId,
    fileUploadType: fileEntry.fileUploadType,
  };
  return fetchApi(`fileEntry/${fileEntry.guid}`, {
    method: "PUT",
    body: JSON.stringify(updatePayload),
  }) as Promise<FileEntry>;
}

/**
 * Get all file entries for a user.
 * If associatedUserId is provided, filters by that user.
 * If undefined, returns all file entries the current user has access to.
 */
export async function getAllFileEntriesForUser(params: {
  associatedUserId?: string;
}): Promise<FileEntry[]> {
  const query = params.associatedUserId
    ? `?associatedUserId=${params.associatedUserId}`
    : "";
  return fetchApi(`fileEntry${query}`) as Promise<FileEntry[]>;
}

/**
 * Get a single file upload via file entry API.
 */
export async function getSingleFileUploadViaFileEntryApi(params: {
  guid: string;
}): Promise<FarmFileUpload> {
  return fetchApi(
    `fileEntry/${params.guid}/fileUpload`
  ) as Promise<FarmFileUpload>;
}

/**
 * Upload a file to get a file upload ID.
 * This uploads the file first and returns a guid that can be used
 * when creating a FileEntry.
 */
export async function uploadFile(params: {
  fileName: string;
  file: string;
}): Promise<{ guid: string }> {
  return fetchApi("fileUpload", {
    method: "POST",
    body: JSON.stringify(params),
  }) as Promise<{ guid: string }>;
}

/**
 * Upload a file to an existing file entry.
 */
export async function uploadFileToEntry(
  fileEntryGuid: string,
  file: File
): Promise<FarmFileUpload> {
  const formData = new FormData();
  formData.append("file", file);

  // Use direct fetch since FormData requires no Content-Type header (browser sets it with boundary)
  const baseUrl = `${import.meta.env.VITE_API_ENDPOINT}/v1/`;
  const res = await fetch(`${baseUrl}fileEntry/${fileEntryGuid}/fileUpload`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${window.localStorage.getItem("jwt") || ""}`,
      // Don't set Content-Type, let the browser set it with boundary for FormData
    },
    body: formData,
  });

  if (!res.ok) {
    const error = await res.json().catch(() => ({ message: res.statusText }));
    throw error;
  }

  return (await res.json()) as FarmFileUpload;
}
